import { useSelector } from 'react-redux'
import { differenceInCalendarDays, differenceInCalendarYears, differenceInDays, differenceInHours, differenceInMinutes, format } from 'date-fns'

import { RootState } from '@Root/slices'
import { MY_HOUSE_AUTH_STATUS, MY_HOUSE_AUTH_TYPE } from '@Root/contants/myhouse'
import { AUTH_STATUS } from '@Root/contants'

export default function useBoard() {
  const myhouse = useSelector((state: RootState) => state.myhouse)
  const { authHouseNoMap, authComplexNoMap, complexNo } = myhouse

  const getRegistrableHouseNo = (houseNo: number | string): boolean => {
    if (!houseNo) {
      return false
    }

    const auth = authHouseNoMap[houseNo]
    if (auth) {
      const authTypeKey = auth?.authType
      const authType = MY_HOUSE_AUTH_TYPE[authTypeKey as keyof typeof MY_HOUSE_AUTH_TYPE]

      if ([MY_HOUSE_AUTH_TYPE.BOTH, MY_HOUSE_AUTH_TYPE.OWNER, MY_HOUSE_AUTH_TYPE.RESIDENT].includes(authType)) {
        return true
      }
    }
    return false
  }

  const getRegistrableComplexNo = (complexNo: number | string): boolean => {
    const authList = authComplexNoMap[complexNo]

    if (!complexNo || !authList) {
      return false
    }

    for (const auth of authList) {
      const authTypeKey = auth?.authType
      const authType = MY_HOUSE_AUTH_TYPE[authTypeKey as keyof typeof MY_HOUSE_AUTH_TYPE]

      if ([MY_HOUSE_AUTH_TYPE.BOTH, MY_HOUSE_AUTH_TYPE.OWNER, MY_HOUSE_AUTH_TYPE.RESIDENT].includes(authType)) {
        return true
      }
    }

    return false
  }

  const getAuthStatusForBoard = (): MY_HOUSE_AUTH_STATUS => {
    const authList = authComplexNoMap[complexNo]
    let isAuthCompletion = false
    let isAuthProgress = false

    for (const auth of authList) {
      const authTypeKey = auth?.authType
      const authType = MY_HOUSE_AUTH_TYPE[authTypeKey as keyof typeof MY_HOUSE_AUTH_TYPE]

      if ([MY_HOUSE_AUTH_TYPE.BOTH, MY_HOUSE_AUTH_TYPE.OWNER, MY_HOUSE_AUTH_TYPE.RESIDENT].includes(authType)) {
        isAuthCompletion = true
        break
      }

      const ownerAuthStatus = auth?.ownerAuthStatus
      const residentAuthStatus = auth?.residentAuthStatus

      const isOwnerAuthProgress = [AUTH_STATUS.APPLY1, AUTH_STATUS.APPLY2].includes(ownerAuthStatus as AUTH_STATUS)
      const isResidentAuthProgress = [AUTH_STATUS.APPLY1, AUTH_STATUS.APPLY2].includes(residentAuthStatus as AUTH_STATUS)

      if (isOwnerAuthProgress || isResidentAuthProgress) {
        isAuthProgress = true
      }
    }

    if (isAuthCompletion) {
      return MY_HOUSE_AUTH_STATUS.COMPLETE
    } else if (isAuthProgress) {
      return MY_HOUSE_AUTH_STATUS.PROGRESS
    } else {
      return MY_HOUSE_AUTH_STATUS.NOT_AUTH
    }
  }

  const getRegistrable = (): boolean => {
    return getRegistrableComplexNo(complexNo)
  }

  const getNoticesReadableHouseNo = (houseNo: number | string): boolean => {
    if (!houseNo) {
      return false
    }

    return !!authHouseNoMap[houseNo]
  }

  const getNoticesReadableComplexNo = (complexNo: number | string): boolean => {
    if (!complexNo) {
      return false
    }

    return !!authComplexNoMap[complexNo]
  }

  const getCurrentDate = (): Date => {
    return new Date()
  }

  const getDifferenceInCalendarYears = (currentDate: Date, targetDate: Date): number => {
    return differenceInCalendarYears(currentDate, targetDate)
  }

  const getDifferenceInDays = (currentDate: Date, targetDate: Date): number => {
    return differenceInDays(currentDate, targetDate)
  }

  const getDifferenceInCalendarDays = (currentDate: Date, targetDate: Date): number => {
    return differenceInCalendarDays(currentDate, targetDate)
  }

  const getDifferenceInHours = (currentDate: Date, targetDate: Date): number => {
    return differenceInHours(currentDate, targetDate)
  }

  const getDifferenceInMinutes = (currentDate: Date, targetDate: Date): number => {
    return differenceInMinutes(currentDate, targetDate)
  }

  const isRecent = (dateTimeString: string) => (dateTimeString ? getDifferenceInDays(getCurrentDate(), getDateObject(dateTimeString)) < 1 : false)

  const setVisited = (sequence: string | number) => {
    // 확인한 게시글 기능 제거. 차후 복원시 주석 해제.
    // localStorage.setItem(`myhouse:bbs:read:${sequence}`, '1')
  }

  const isVisited = (sequence: string | number) => {
    // 확인한 게시글 기능 제거. 차후 복원시 주석 해제.
    // !!localStorage.getItem(`myhouse:bbs:read:${sequence}`)
    return false
  }

  const getDateObject = (dateTimeString: string): Date => {
    return new Date(dateTimeString)
  }

  const getSimplifiedDate = (dateTimeString: string) => {
    if (!dateTimeString) {
      return ''
    }

    const current = getCurrentDate()
    const target = getDateObject(dateTimeString)

    if (getDifferenceInCalendarYears(current, target)) {
      return format(getDateObject(dateTimeString), 'yyyy.MM.dd.')
    }

    if (getDifferenceInCalendarDays(current, target)) {
      return format(getDateObject(dateTimeString), 'MM.dd.')
    }

    const differenceInHours = getDifferenceInHours(current, target)

    if (differenceInHours) {
      return `${differenceInHours}시간 전`
    }

    const differenceInMinutes = getDifferenceInMinutes(current, target)

    if (differenceInMinutes) {
      return `${differenceInMinutes}분 전`
    }

    return '방금전'
  }

  const getFormattedDate = (dateTimeString: string) => dateTimeString && format(getDateObject(dateTimeString), 'yy.MM.dd HH:mm')

  return {
    getRegistrableHouseNo,
    getRegistrableComplexNo,
    getRegistrable,
    getAuthStatusForBoard,
    getNoticesReadableHouseNo,
    getNoticesReadableComplexNo,
    isRecent,
    setVisited,
    isVisited,
    getDateObject,
    getSimplifiedDate,
    getFormattedDate,
  }
}
